import React, { useState, useEffect } from 'react';
import {
  FaBell, FaBan, FaCoins, FaMedal, FaCalendarTimes,
  FaShieldAlt, FaExclamationTriangle, FaSearch, FaCheck,
  FaPowerOff, FaTools, FaTrash,
} from 'react-icons/fa';
import { supabase } from '../services/supabase';
import { useAuth } from '../context/AuthContext';

// ─── helpers ────────────────────────────────────────────────────────────────
function Toast({ msg, type }) {
  return msg ? (
    <div style={{ ...ts.toast, backgroundColor: type === 'error' ? '#fee2e2' : '#dcfce7', color: type === 'error' ? '#dc2626' : '#16a34a' }}>
      {type === 'error' ? '✗' : '✓'} {msg}
    </div>
  ) : null;
}

function SectionCard({ title, icon: Icon, color = '#6366f1', children }) {
  return (
    <div style={ts.sCard}>
      <div style={{ ...ts.sCardHeader, borderLeft: `4px solid ${color}` }}>
        <Icon size={16} color={color} />
        <h3 style={ts.sCardTitle}>{title}</h3>
      </div>
      <div style={ts.sCardBody}>{children}</div>
    </div>
  );
}

export default function InterventionsPage() {
  const { admin } = useAuth();
  const [toast, setToast] = useState({ msg: '', type: 'success' });
  const [loading, setLoading] = useState({});

  // Notification state
  const [notifTarget, setNotifTarget] = useState('all_users');
  const [notifSearch, setNotifSearch] = useState('');
  const [notifSearchResults, setNotifSearchResults] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedBiz, setSelectedBiz] = useState(null);
  const [notifTitle, setNotifTitle] = useState('');
  const [notifBody, setNotifBody] = useState('');
  const [notifType, setNotifType] = useState('info');

  // Points adjustment
  const [pointsUserId, setPointsUserId] = useState('');
  const [pointsSearch, setPointsSearch] = useState('');
  const [pointsUser, setPointsUser] = useState(null);
  const [pointsDelta, setPointsDelta] = useState('');
  const [pointsReason, setPointsReason] = useState('');

  // Badge grant
  const [badgeUserId, setBadgeUserId] = useState('');
  const [badgeSearch, setBadgeSearch] = useState('');
  const [badgeUser, setBadgeUser] = useState(null);
  const [badgeName, setBadgeName] = useState('');

  // Subscription
  const [subBizId, setSubBizId] = useState('');
  const [subBizSearch, setSubBizSearch] = useState('');
  const [subBiz, setSubBiz] = useState(null);
  const [subMonths, setSubMonths] = useState('1');

  // Emergency
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [maintenanceMsg, setMaintenanceMsg] = useState('Sistem texniki xidmətdədir. Qısa müddətdə geri qayıdacağıq.');
  const [maintenanceLoading, setMaintenanceLoading] = useState(false);

  useEffect(() => {
    fetchMaintenanceMode();
  }, []);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast({ msg: '', type: 'success' }), 3500);
  };

  const setLoad = (key, val) => setLoading(prev => ({ ...prev, [key]: val }));

  // ── Maintenance mode ──────────────────────────────────────────────────────
  const fetchMaintenanceMode = async () => {
    const { data } = await supabase
      .from('system_config')
      .select('value')
      .eq('key', 'maintenance_mode')
      .single();
    if (data) setMaintenanceMode(data.value === 'true' || data.value === true);
  };

  const toggleMaintenance = async () => {
    setMaintenanceLoading(true);
    const newVal = !maintenanceMode;
    const { error } = await supabase
      .from('system_config')
      .upsert({ key: 'maintenance_mode', value: String(newVal), updated_at: new Date().toISOString() }, { onConflict: 'key' });
    if (!error) {
      // Also store maintenance message
      await supabase
        .from('system_config')
        .upsert({ key: 'maintenance_message', value: maintenanceMsg, updated_at: new Date().toISOString() }, { onConflict: 'key' });
      setMaintenanceMode(newVal);
      await logAction(newVal ? 'enable_maintenance' : 'disable_maintenance', 'system', null, { message: maintenanceMsg });
      showToast(newVal ? '🔴 Maintenance mode aktiv edildi' : '🟢 Maintenance mode söndürüldü');
    } else {
      showToast('Xəta: ' + error.message, 'error');
    }
    setMaintenanceLoading(false);
  };

  // ── User search (shared) ──────────────────────────────────────────────────
  const searchUsers = async (q, setResults) => {
    if (!q.trim()) { setResults([]); return; }
    const { data } = await supabase
      .from('users')
      .select('id, full_name, email, points')
      .or(`full_name.ilike.%${q}%,email.ilike.%${q}%`)
      .limit(6);
    setResults(data || []);
  };

  const searchBusinesses = async (q, setResults) => {
    if (!q.trim()) { setResults([]); return; }
    const { data } = await supabase
      .from('businesses')
      .select('id, name, subscription_end')
      .ilike('name', `%${q}%`)
      .limit(6);
    setResults(data || []);
  };

  // ── Send Notification ─────────────────────────────────────────────────────
  const sendNotification = async () => {
    if (!notifTitle.trim() || !notifBody.trim()) { showToast('Başlıq və mətn daxil edin', 'error'); return; }
    setLoad('notif', true);
    try {
      let rows = [];
      if (notifTarget === 'all_users') {
        const { data: allUsers } = await supabase.from('users').select('id');
        rows = allUsers?.map(u => ({ user_id: u.id, type: notifType, title: notifTitle, body: notifBody, is_read: false })) || [];
      } else if (notifTarget === 'specific_user' && selectedUser) {
        rows = [{ user_id: selectedUser.id, type: notifType, title: notifTitle, body: notifBody, is_read: false }];
      } else if (notifTarget === 'all_businesses') {
        const { data: bizOwners } = await supabase.from('businesses').select('owner_id').eq('is_active', true);
        const ownerIds = [...new Set(bizOwners?.map(b => b.owner_id))];
        rows = ownerIds.map(uid => ({ user_id: uid, type: notifType, title: notifTitle, body: notifBody, is_read: false }));
      } else {
        showToast('Hədəf seçin', 'error'); setLoad('notif', false); return;
      }

      if (rows.length === 0) { showToast('Uyğun alıcı tapılmadı', 'error'); setLoad('notif', false); return; }

      // Insert in chunks of 500
      for (let i = 0; i < rows.length; i += 500) {
        await supabase.from('notifications').insert(rows.slice(i, i + 500));
      }

      await logAction('send_notification', 'notification', null, { target: notifTarget, title: notifTitle, count: rows.length });
      showToast(`✓ ${rows.length} alıcıya bildiriş göndərildi`);
      setNotifTitle(''); setNotifBody('');
    } catch (e) {
      showToast('Xəta: ' + e.message, 'error');
    }
    setLoad('notif', false);
  };

  // ── Points adjustment ─────────────────────────────────────────────────────
  const [pointsResults, setPointsResults] = useState([]);

  const adjustPoints = async () => {
    if (!pointsUser) { showToast('İstifadəçi seçin', 'error'); return; }
    const delta = parseInt(pointsDelta);
    if (isNaN(delta)) { showToast('Etibarlı rəqəm daxil edin', 'error'); return; }
    setLoad('points', true);
    const { error } = await supabase
      .from('users')
      .update({ points: Math.max(0, (pointsUser.points || 0) + delta) })
      .eq('id', pointsUser.id);
    if (!error) {
      await logAction('adjust_points', 'user', pointsUser.id, { delta, reason: pointsReason });
      showToast(`${delta > 0 ? '+' : ''}${delta} xal tətbiq edildi`);
      setPointsUser(prev => ({ ...prev, points: Math.max(0, (prev.points || 0) + delta) }));
      setPointsDelta(''); setPointsReason('');
    } else {
      showToast('Xəta: ' + error.message, 'error');
    }
    setLoad('points', false);
  };

  // ── Badge grant ───────────────────────────────────────────────────────────
  const [badgeResults, setBadgeResults] = useState([]);
  const BADGE_OPTIONS = ['Explorer', 'Social Butterfly', 'Loyal Customer', 'Early Bird', 'Weekend Warrior', 'VIP', 'Pioneer'];

  const grantBadge = async () => {
    if (!badgeUser || !badgeName) { showToast('İstifadəçi və badge seçin', 'error'); return; }
    setLoad('badge', true);
    const { error } = await supabase
      .from('user_badges')
      .upsert({ user_id: badgeUser.id, badge_name: badgeName, earned_at: new Date().toISOString() }, { onConflict: 'user_id,badge_name' });
    if (!error) {
      await logAction('grant_badge', 'user', badgeUser.id, { badge: badgeName });
      showToast(`"${badgeName}" badge verildi`);
      setBadgeUser(null); setBadgeName(''); setBadgeSearch('');
    } else {
      showToast('Xəta: ' + error.message, 'error');
    }
    setLoad('badge', false);
  };

  // ── Subscription extend ───────────────────────────────────────────────────
  const [subResults, setSubResults] = useState([]);

  const extendSubscription = async () => {
    if (!subBiz) { showToast('Biznes seçin', 'error'); return; }
    const months = parseInt(subMonths);
    if (isNaN(months) || months < 1) { showToast('Etibarlı ay sayı daxil edin', 'error'); return; }
    setLoad('sub', true);
    const base = subBiz.subscription_end ? new Date(subBiz.subscription_end) : new Date();
    if (base < new Date()) base.setTime(Date.now());
    base.setMonth(base.getMonth() + months);
    const { error } = await supabase
      .from('businesses')
      .update({ subscription_end: base.toISOString(), is_active: true })
      .eq('id', subBiz.id);
    if (!error) {
      await logAction('extend_subscription', 'business', subBiz.id, { months, new_end: base.toISOString() });
      showToast(`Abunəlik ${months} ay uzadıldı → ${base.toLocaleDateString('az-AZ')}`);
      setSubBiz(null); setSubBizSearch(''); setSubMonths('1');
    } else {
      showToast('Xəta: ' + error.message, 'error');
    }
    setLoad('sub', false);
  };

  const forceExpireSubscription = async () => {
    if (!subBiz) { showToast('Biznes seçin', 'error'); return; }
    if (!window.confirm(`"${subBiz.name}" biznesi deaktiv edilsin?`)) return;
    setLoad('expire', true);
    const { error } = await supabase
      .from('businesses')
      .update({ subscription_end: new Date().toISOString(), is_active: false })
      .eq('id', subBiz.id);
    if (!error) {
      await logAction('force_expire_subscription', 'business', subBiz.id, {});
      showToast(`Abunəlik dayandırıldı — biznes deaktiv`);
      setSubBiz(null); setSubBizSearch('');
    } else {
      showToast('Xəta: ' + error.message, 'error');
    }
    setLoad('expire', false);
  };

  // ── Purge old check-ins ───────────────────────────────────────────────────
  const purgeOldCheckIns = async (days) => {
    if (!window.confirm(`${days} gündən köhnə check-inlər silinsin?`)) return;
    setLoad('purge', true);
    const cutoff = new Date(Date.now() - days * 86400000).toISOString();
    const { error, count } = await supabase
      .from('check_ins')
      .delete({ count: 'exact' })
      .lt('created_at', cutoff);
    if (!error) {
      await logAction('purge_checkins', 'system', null, { days, cutoff });
      showToast(`${count || 0} köhnə check-in silindi`);
    } else {
      showToast('Xəta: ' + error.message, 'error');
    }
    setLoad('purge', false);
  };

  const resolveAllFraud = async () => {
    if (!window.confirm('Bütün həll edilməmiş fraud xəbərdarlıqları "resolved" edilsin?')) return;
    setLoad('fraud', true);
    const { error } = await supabase.from('fraud_logs').update({ resolved: true }).eq('resolved', false);
    if (!error) {
      await logAction('resolve_all_fraud', 'system', null, {});
      showToast('Bütün fraud xəbərdarlıqları həll edildi');
    } else showToast('Xəta: ' + error.message, 'error');
    setLoad('fraud', false);
  };

  // ── Log ───────────────────────────────────────────────────────────────────
  const logAction = async (action, targetType, targetId, details) => {
    await supabase.from('system_logs').insert({
      admin_id: admin?.id,
      action,
      target_type: targetType,
      target_id: targetId,
      details,
      created_at: new Date().toISOString(),
    });
  };

  // ── Dropdown helper ───────────────────────────────────────────────────────
  function SearchDropdown({ results, onSelect, renderItem }) {
    if (!results.length) return null;
    return (
      <div style={ts.dropdown}>
        {results.map((r, i) => (
          <div key={i} style={ts.dropdownItem} onClick={() => onSelect(r)}>{renderItem(r)}</div>
        ))}
      </div>
    );
  }

  return (
    <div style={ts.page}>
      {/* Toast */}
      <Toast msg={toast.msg} type={toast.type} />

      {/* Header */}
      <div style={ts.header}>
        <h1 style={ts.title}><FaTools size={20} color="#6366f1" /> Müdaxilələr</h1>
        <p style={ts.sub}>Sistemə birbaşa müdaxilə, bildiriş göndərmə və manual tənzimləmələr</p>
      </div>

      <div style={ts.grid}>

        {/* ── Emergency Controls ─────────────────────────────────────────── */}
        <SectionCard title="Fövqəladə İdarəetmə" icon={FaPowerOff} color="#ef4444">
          <div style={ts.warningBox}>
            <FaExclamationTriangle color="#f59e0b" size={16} />
            <span>Bu hərəkətlər bütün platformaya dərhal təsir edir!</span>
          </div>
          <div style={ts.field}>
            <label style={ts.label}>Maintenance Mesajı</label>
            <textarea
              value={maintenanceMsg}
              onChange={e => setMaintenanceMsg(e.target.value)}
              style={ts.textarea}
              rows={2}
            />
          </div>
          <button
            onClick={toggleMaintenance}
            disabled={maintenanceLoading}
            style={{ ...ts.btn, backgroundColor: maintenanceMode ? '#22c55e' : '#ef4444', width: '100%' }}
          >
            <FaPowerOff size={13} />
            {maintenanceLoading ? 'Tətbiq edilir...' : maintenanceMode ? '✓ Maintenance AKTİV — Söndür' : 'Maintenance Mode Aktiv Et'}
          </button>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 12 }}>
            <button onClick={resolveAllFraud} disabled={loading.fraud} style={{ ...ts.btn, backgroundColor: '#f59e0b' }}>
              <FaCheck size={12} /> {loading.fraud ? '...' : 'Fraud Sıfırla'}
            </button>
            <button onClick={() => purgeOldCheckIns(90)} disabled={loading.purge} style={{ ...ts.btn, backgroundColor: '#64748b' }}>
              <FaTrash size={12} /> {loading.purge ? '...' : '90g+ Check-in Sil'}
            </button>
          </div>
        </SectionCard>

        {/* ── Send Notification ──────────────────────────────────────────── */}
        <SectionCard title="Bildiriş Göndər" icon={FaBell} color="#6366f1">
          <div style={ts.field}>
            <label style={ts.label}>Hədəf</label>
            <select value={notifTarget} onChange={e => { setNotifTarget(e.target.value); setSelectedUser(null); setSelectedBiz(null); }} style={ts.select}>
              <option value="all_users">Bütün istifadəçilər</option>
              <option value="all_businesses">Bütün biznes sahibləri</option>
              <option value="specific_user">Xüsusi istifadəçi</option>
            </select>
          </div>
          {notifTarget === 'specific_user' && (
            <div style={{ position: 'relative', marginBottom: 10 }}>
              <div style={ts.searchRow}>
                <FaSearch size={12} color="#94a3b8" />
                <input
                  style={ts.searchInput}
                  placeholder="Ad və ya email..."
                  value={selectedUser ? `${selectedUser.full_name || selectedUser.email}` : notifSearch}
                  onChange={e => { setNotifSearch(e.target.value); setSelectedUser(null); searchUsers(e.target.value, setNotifSearchResults); }}
                />
              </div>
              <SearchDropdown results={notifSearchResults} onSelect={u => { setSelectedUser(u); setNotifSearchResults([]); setNotifSearch(''); }} renderItem={u => <>{u.full_name || '—'} <span style={{ color: '#94a3b8', fontSize: 12 }}>{u.email}</span></>} />
            </div>
          )}
          <div style={ts.field}>
            <label style={ts.label}>Tip</label>
            <select value={notifType} onChange={e => setNotifType(e.target.value)} style={ts.select}>
              <option value="info">ℹ️ Məlumat</option>
              <option value="warning">⚠️ Xəbərdarlıq</option>
              <option value="promo">🎁 Promosyon</option>
              <option value="system">⚙️ Sistem</option>
            </select>
          </div>
          <div style={ts.field}>
            <label style={ts.label}>Başlıq</label>
            <input value={notifTitle} onChange={e => setNotifTitle(e.target.value)} style={ts.input} placeholder="Bildiriş başlığı..." />
          </div>
          <div style={ts.field}>
            <label style={ts.label}>Mətn</label>
            <textarea value={notifBody} onChange={e => setNotifBody(e.target.value)} style={ts.textarea} rows={3} placeholder="Bildiriş mətni..." />
          </div>
          <button onClick={sendNotification} disabled={loading.notif} style={{ ...ts.btn, width: '100%' }}>
            <FaBell size={13} /> {loading.notif ? 'Göndərilir...' : 'Göndər'}
          </button>
        </SectionCard>

        {/* ── Points Adjustment ─────────────────────────────────────────── */}
        <SectionCard title="Xal Tənzimlənməsi" icon={FaCoins} color="#f59e0b">
          <div style={{ position: 'relative', marginBottom: 10 }}>
            <div style={ts.searchRow}>
              <FaSearch size={12} color="#94a3b8" />
              <input
                style={ts.searchInput}
                placeholder="İstifadəçi axtar..."
                value={pointsUser ? `${pointsUser.full_name || pointsUser.email}` : pointsSearch}
                onChange={e => { setPointsSearch(e.target.value); setPointsUser(null); searchUsers(e.target.value, setPointsResults); }}
              />
            </div>
            <SearchDropdown results={pointsResults} onSelect={u => { setPointsUser(u); setPointsResults([]); setPointsSearch(''); }} renderItem={u => <>{u.full_name || '—'} <span style={{ color: '#94a3b8' }}>({u.points || 0} xal)</span></>} />
          </div>
          {pointsUser && (
            <div style={ts.selectedChip}>
              <FaCheck size={11} color="#22c55e" />
              <strong>{pointsUser.full_name || pointsUser.email}</strong>
              <span style={{ color: '#64748b' }}>— cari: {pointsUser.points || 0} xal</span>
            </div>
          )}
          <div style={ts.twoFields}>
            <div style={ts.field}>
              <label style={ts.label}>Dəyişim (+/-)</label>
              <input value={pointsDelta} onChange={e => setPointsDelta(e.target.value)} style={ts.input} placeholder="+100 və ya -50" type="number" />
            </div>
            <div style={ts.field}>
              <label style={ts.label}>Səbəb</label>
              <input value={pointsReason} onChange={e => setPointsReason(e.target.value)} style={ts.input} placeholder="Könüllü..." />
            </div>
          </div>
          <button onClick={adjustPoints} disabled={loading.points} style={{ ...ts.btn, width: '100%', backgroundColor: '#f59e0b' }}>
            <FaCoins size={13} /> {loading.points ? 'Tətbiq edilir...' : 'Tətbiq Et'}
          </button>
        </SectionCard>

        {/* ── Badge Grant ───────────────────────────────────────────────── */}
        <SectionCard title="Badge Ver" icon={FaMedal} color="#8b5cf6">
          <div style={{ position: 'relative', marginBottom: 10 }}>
            <div style={ts.searchRow}>
              <FaSearch size={12} color="#94a3b8" />
              <input
                style={ts.searchInput}
                placeholder="İstifadəçi axtar..."
                value={badgeUser ? `${badgeUser.full_name || badgeUser.email}` : badgeSearch}
                onChange={e => { setBadgeSearch(e.target.value); setBadgeUser(null); searchUsers(e.target.value, setBadgeResults); }}
              />
            </div>
            <SearchDropdown results={badgeResults} onSelect={u => { setBadgeUser(u); setBadgeResults([]); setBadgeSearch(''); }} renderItem={u => <>{u.full_name || '—'} <span style={{ color: '#94a3b8', fontSize: 12 }}>{u.email}</span></>} />
          </div>
          {badgeUser && (
            <div style={ts.selectedChip}>
              <FaCheck size={11} color="#22c55e" /> <strong>{badgeUser.full_name || badgeUser.email}</strong>
            </div>
          )}
          <div style={ts.field}>
            <label style={ts.label}>Badge</label>
            <select value={badgeName} onChange={e => setBadgeName(e.target.value)} style={ts.select}>
              <option value="">Seçin...</option>
              {BADGE_OPTIONS.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>
          <button onClick={grantBadge} disabled={loading.badge} style={{ ...ts.btn, width: '100%', backgroundColor: '#8b5cf6' }}>
            <FaMedal size={13} /> {loading.badge ? 'Verilir...' : 'Badge Ver'}
          </button>
        </SectionCard>

        {/* ── Subscription Management ───────────────────────────────────── */}
        <SectionCard title="Abunəlik İdarəetməsi" icon={FaCalendarTimes} color="#10b981">
          <div style={{ position: 'relative', marginBottom: 10 }}>
            <div style={ts.searchRow}>
              <FaSearch size={12} color="#94a3b8" />
              <input
                style={ts.searchInput}
                placeholder="Biznes axtar..."
                value={subBiz ? subBiz.name : subBizSearch}
                onChange={e => { setSubBizSearch(e.target.value); setSubBiz(null); searchBusinesses(e.target.value, setSubResults); }}
              />
            </div>
            <SearchDropdown results={subResults} onSelect={b => { setSubBiz(b); setSubResults([]); setSubBizSearch(''); }} renderItem={b => <>{b.name} <span style={{ color: '#94a3b8', fontSize: 12 }}>{b.subscription_end ? `→ ${new Date(b.subscription_end).toLocaleDateString('az-AZ')}` : 'tarix yoxdur'}</span></>} />
          </div>
          {subBiz && (
            <div style={ts.selectedChip}>
              <FaCheck size={11} color="#22c55e" /> <strong>{subBiz.name}</strong>
              {subBiz.subscription_end && <span style={{ color: '#64748b', fontSize: 12 }}>— bitmə: {new Date(subBiz.subscription_end).toLocaleDateString('az-AZ')}</span>}
            </div>
          )}
          <div style={ts.field}>
            <label style={ts.label}>Uzatma (ay)</label>
            <input value={subMonths} onChange={e => setSubMonths(e.target.value)} style={ts.input} type="number" min="1" max="24" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <button onClick={extendSubscription} disabled={loading.sub} style={{ ...ts.btn, backgroundColor: '#10b981' }}>
              {loading.sub ? '...' : 'Uzat'}
            </button>
            <button onClick={forceExpireSubscription} disabled={loading.expire} style={{ ...ts.btn, backgroundColor: '#ef4444' }}>
              <FaBan size={12} /> {loading.expire ? '...' : 'Dayandır'}
            </button>
          </div>
        </SectionCard>

        {/* ── Security ─────────────────────────────────────────────────── */}
        <SectionCard title="Təhlükəsizlik Əməliyyatları" icon={FaShieldAlt} color="#0ea5e9">
          <p style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>
            Şübhəli davranış zamanı tez müdaxilə alətləri.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <button onClick={() => purgeOldCheckIns(30)} disabled={loading.purge} style={{ ...ts.btn, backgroundColor: '#0ea5e9' }}>
              <FaTrash size={12} /> {loading.purge ? 'Silinir...' : '30 gündən köhnə check-inləri sil'}
            </button>
            <button onClick={() => purgeOldCheckIns(7)} disabled={loading.purge} style={{ ...ts.btnDanger }}>
              <FaTrash size={12} /> 7 gündən köhnə check-inləri sil (test üçün)
            </button>
            <button onClick={resolveAllFraud} disabled={loading.fraud} style={{ ...ts.btn, backgroundColor: '#64748b' }}>
              <FaCheck size={12} /> {loading.fraud ? '...' : 'Bütün fraud xəbərdarlıqlarını həll et'}
            </button>
          </div>
        </SectionCard>

      </div>
    </div>
  );
}

// ─── BADGE_OPTIONS used in render ─────────────────────────────────────────
const BADGE_OPTIONS = ['Explorer', 'Social Butterfly', 'Loyal Customer', 'Early Bird', 'Weekend Warrior', 'VIP', 'Pioneer'];

const ts = {
  page: { padding: 28, minHeight: '100vh', backgroundColor: '#f8fafc' },
  header: { marginBottom: 28 },
  title: { fontSize: 24, fontWeight: 800, color: '#0f172a', margin: 0, display: 'flex', alignItems: 'center', gap: 10 },
  sub: { fontSize: 13, color: '#64748b', marginTop: 6 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 20 },
  sCard: { backgroundColor: '#fff', borderRadius: 14, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' },
  sCardHeader: { display: 'flex', alignItems: 'center', gap: 10, padding: '14px 20px', backgroundColor: '#f8fafc', borderBottom: '1px solid #f1f5f9', paddingLeft: 16 },
  sCardTitle: { fontSize: 14, fontWeight: 700, color: '#1e293b', margin: 0 },
  sCardBody: { padding: 20 },
  field: { marginBottom: 12 },
  label: { display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 5 },
  input: { width: '100%', padding: '9px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, color: '#1e293b', boxSizing: 'border-box', outline: 'none' },
  select: { width: '100%', padding: '9px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, color: '#1e293b', boxSizing: 'border-box', outline: 'none', backgroundColor: '#fff' },
  textarea: { width: '100%', padding: '9px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, color: '#1e293b', boxSizing: 'border-box', outline: 'none', resize: 'vertical', fontFamily: 'inherit' },
  btn: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7, padding: '9px 16px', backgroundColor: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 },
  btnDanger: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7, padding: '9px 16px', backgroundColor: '#fff', color: '#ef4444', border: '1.5px solid #ef4444', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 },
  twoFields: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 },
  searchRow: { display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, backgroundColor: '#fff' },
  searchInput: { flex: 1, border: 'none', outline: 'none', fontSize: 13, color: '#1e293b' },
  dropdown: { position: 'absolute', top: '100%', left: 0, right: 0, backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', zIndex: 100, overflow: 'hidden' },
  dropdownItem: { padding: '10px 14px', cursor: 'pointer', fontSize: 13, color: '#1e293b', borderBottom: '1px solid #f8fafc' },
  selectedChip: { display: 'flex', alignItems: 'center', gap: 6, backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '7px 12px', fontSize: 13, marginBottom: 12 },
  warningBox: { display: 'flex', alignItems: 'center', gap: 8, backgroundColor: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#92400e', marginBottom: 14 },
  toast: { position: 'fixed', bottom: 24, right: 24, padding: '12px 20px', borderRadius: 10, fontSize: 14, fontWeight: 600, zIndex: 9999, boxShadow: '0 4px 16px rgba(0,0,0,0.12)' },
};
